import React, { useEffect, useState, useImperativeHandle, forwardRef, } from 'react';
import Button from '../Button/Button';
import './AnalyseOffreTechnique.css';

// Types et interfaces
interface Presta {
  id: string;
  id_status: number;
  nom: string;
  organisme: string;
  offreTechnique: {
    reponseTechnique: string;
  };
  dateReponse: string;
}

interface CritereNote {
  note?: number;
  commentaire?: string;
  description?: string;
}

interface TechNotes {
  [prestaId: string]: {
    [critere: string]: CritereNote;
  };
}

interface CritereOption {
  value: number;
  label: string;
}

interface Critere {
  key: string;
  label: string;
  maxPoints: number;
  options: CritereOption[];
  description: string;
}

interface AnalyseOffreTechniqueProps {
  prestataires: Presta[];
  techNotes: TechNotes;
  setTechNotes: React.Dispatch<React.SetStateAction<TechNotes>>;
  onNext: () => void;
  viewOnly?: boolean;
}

// Constantes
const CRITERES: Critere[] = [
  {
    key: 'organisation',
    label: 'Modes Opératoires pour l\'éxecution du marché',
    maxPoints: 5,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1.25, label: '1,25 - Pas satisfaisant' },
      { value: 2.5, label: '2,50 - Peu satisfaisant' },
      { value: 3.75, label: '3,75 - Assez satisfaisant' },
      { value: 5, label: '5,00 - Satisfaisant' },
    ],
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus vestibulum enim vitae tincidunt consequat. Maecenas vitae sem id eros ultricies accumsan vel eu nulla. Donec accumsan arcu sem, et tempor purus sagittis non. Vivamus quis efficitur odio, vel placerat mauris. Sed finibus risus ac lorem porta commodo. Donec sollicitudin blandit sollicitudin. Pellentesque tempus sit amet lacus eu pretium. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc congue nulla eu rutrum feugiat. In euismod venenatis purus ac cursus."
  },
  {
    key: 'moyens',
    label: 'Moyens Humains et Materiels dédiés au marché',
    maxPoints: 4,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 1, label: '1,00 - Pas satisfaisant' },
      { value: 2, label: '2,00 - Peu satisfaisant' },
      { value: 3, label: '3,00 - Assez satisfaisant' },
      { value: 4, label: '4,00 - Satisfaisant' },
    ],
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus vestibulum enim vitae tincidunt consequat. Maecenas vitae sem id eros ultricies accumsan vel eu nulla. Donec accumsan arcu sem, et tempor purus sagittis non. Vivamus quis efficitur odio, vel placerat mauris. Sed finibus risus ac lorem porta commodo. Donec sollicitudin blandit sollicitudin. Pellentesque tempus sit amet lacus eu pretium. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc congue nulla eu rutrum feugiat. In euismod venenatis purus ac cursus."

  },
  {
    key: 'social',
    label: 'Mesures en matière Environnementale & Sociale',
    maxPoints: 1,
    options: [
      { value: 0, label: '0,00 - Non fournis' },
      { value: 0.25, label: '0,25 - Pas satisfaisant' },
      { value: 0.5, label: '0,50 - Peu satisfaisant' },
      { value: 0.75, label: '0,75 - Assez satisfaisant' },
      { value: 1, label: '1,00 - Satisfaisant' },
    ],
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus vestibulum enim vitae tincidunt consequat. Maecenas vitae sem id eros ultricies accumsan vel eu nulla. Donec accumsan arcu sem, et tempor purus sagittis non. Vivamus quis efficitur odio, vel placerat mauris. Sed finibus risus ac lorem porta commodo. Donec sollicitudin blandit sollicitudin. Pellentesque tempus sit amet lacus eu pretium. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc congue nulla eu rutrum feugiat. In euismod venenatis purus ac cursus."
  }
];

/**
 * Composant pour l'analyse des offres techniques des prestataires
 */
const AnalyseOffreTechnique = forwardRef<{
  validate: () => boolean;
}, AnalyseOffreTechniqueProps>(({
  prestataires,
  techNotes,
  setTechNotes,
  onNext,
  viewOnly = false,
}, ref) => {
  // États
  const [criteresInfo, setCriteresInfo] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [globalError, setGlobalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Effets
  useEffect(() => {
    // Chargement des informations des critères
    const loadCriteresInfo = async () => {
      try {
        // Ici on pourrait appeler une API réelle
        // Pour l'instant, on initialise avec des objets vides
        setCriteresInfo({
          organisation: "",
          moyens: "",
          social: ""
        });
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement des critères:", error);
        setLoading(false);
      }
    };

    loadCriteresInfo();
  }, []);

  // Fonctions utilitaires
  /**
   * Calcule la note globale d'un prestataire sur 10
   */
  const getNoteGlobale = (prestaId: string): string => {
    let totalPoints = 0;
    let maxPossiblePoints = 0;

    CRITERES.forEach(critere => {
      const note = techNotes[prestaId]?.[critere.key]?.note || 0;
      totalPoints += note;
      maxPossiblePoints += critere.maxPoints;
    });

    return maxPossiblePoints > 0 ?
      ((totalPoints / maxPossiblePoints) * 10).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) :
      "0,00";
  };

  /**
   * Valide les notes et commentaires des prestataires
   */
  const validate = (): boolean => {
    let hasError = false;
    const newErrors: Record<string, any> = {};
    let firstErrorElementId: string | null = null;

    prestataires.forEach((presta) => {
      newErrors[presta.id] = {};

      CRITERES.forEach(({ key }) => {
        const note = techNotes[presta.id]?.[key]?.note;
        const commentaire = techNotes[presta.id]?.[key]?.commentaire;
        newErrors[presta.id][key] = {};

        if (note === undefined || isNaN(note)) {
          newErrors[presta.id][key].note = 'La note est requise';
          hasError = true;

          if (!firstErrorElementId) {
            firstErrorElementId = `${presta.id}-${key}-note`;
          }
        }

        if (!commentaire || commentaire.trim() === '') {
          newErrors[presta.id][key].commentaire = 'Le commentaire est requis';
          hasError = true;

          if (!firstErrorElementId) {
            firstErrorElementId = `${presta.id}-${key}-commentaire`;
          }
        }
      });
    });

    setErrors(newErrors);

    if (hasError) {
      setGlobalError('Merci de remplir tous les champs obligatoires.');
      if (firstErrorElementId) {
        setTimeout(() => {
          document.getElementById(firstErrorElementId)?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    }

    return !hasError;
  };

  useImperativeHandle(ref, () => ({
    validate
  }));

  // Gestionnaires d'événements
  /**
   * Gère la mise à jour des notes ou commentaires
   */
  const handleChange = (prestaId: string, critere: string, field: string, value: any) => {
    setTechNotes((prev) => ({
      ...prev,
      [prestaId]: {
        ...prev[prestaId],
        [critere]: {
          ...((prev[prestaId] && prev[prestaId][critere]) || {}),
          [field]: value
        }
      }
    }));

    // Réinitialise les erreurs pour ce champ
    setErrors((prev) => ({
      ...prev,
      [prestaId]: {
        ...prev[prestaId],
        [critere]: {
          ...((prev[prestaId] && prev[prestaId][critere]) || {}),
          [field]: ''
        }
      }
    }));

    setGlobalError('');
  };

  /**
   * Gère le changement de note
   */
  const handleNoteChange = (prestaId: string, critere: string, value: string) => {
    handleChange(prestaId, critere, 'note', value === '' ? '' : Number(value));
  };

  /**
   * Gère le changement de commentaire
   */
  const handleCommentChange = (prestaId: string, critere: string, value: string) => {
    handleChange(prestaId, critere, 'commentaire', value);
  };

  /**
   * Gère la navigation vers l'étape suivante
   */
  const handleNextClick = () => {
    onNext();
  };

  /**
   * Sauvegarde les données en brouillon
   */
  const handleSaveDraft = async () => {
    setIsSaving(true);
    setSuccessMsg('');

    try {
      const response = await fetch('/api/marche-subsequent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'Brouillon',
          techNotes: techNotes
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      setSuccessMsg('Votre brouillon a bien été sauvegardé.');
    } catch (error) {
      setSuccessMsg("Erreur lors de la sauvegarde du brouillon.");
    } finally {
      setIsSaving(false);
    }
  };

  // Affiche un indicateur de chargement si les données ne sont pas prêtes
  if (loading) {
    return <div className="loader-container">Chargement des critères...</div>;
  }

  // Vérifier si des prestataires sont disponibles
  if (!prestataires || prestataires.length === 0) {
    return <div className="no-data">Aucune offre technique disponible pour cette opération.</div>;
  }

  // Rendu du composant
  return (
    <div className="step-container">
      {/* Message d'erreur global */}
      {globalError && (
        <div className="error-message validation-error" style={{ marginBottom: '2rem' }}>
          {globalError}
        </div>
      )}

      {/* Message de succès */}
      {successMsg && (
        <div className="success-message" style={{ marginBottom: '2rem' }}>
          {successMsg}
        </div>
      )}

      {/* Grille de comparaison des prestataires */}
      <div className="presta-comparaison-grid">
        {prestataires.map((presta) => {
          const isExpired = presta.id_status === 12;
          return (
            <div className={`presta-col ${isExpired ? "disabled" : ""}`} key={presta.id}>

              {/* Ajout du badge pour les commandes expirées */}
              {isExpired && <div className="expired-badge">Expiré</div>}

              {/* En-tête du prestataire */}
              <div className="presta-header">
                <h3>{presta.organisme}</h3>
                {isExpired ? (
                  <span>Offre non reçue</span>
                ) : (
                  <span>Offre reçue le {presta?.dateReponse}</span>
                )
                }
              </div>

              {/* Réponse technique */}
              <div className="form-group">
                <label>Réponse technique au besoin :</label>
                <div className="text-other-user">
                  {presta.offreTechnique.reponseTechnique || "Aucune réponse technique fournie."}
                </div>
              </div>

              <hr className='darkblue' />

              {/* Critères d'analyse */}
              {CRITERES.map(({ key, label, description, maxPoints, options }) => (
                <div key={key} className="form-group">
                  <span className="label">{label}</span>
                  <div className="text-other-user">
                    {techNotes[presta.id]?.[key]?.description || description || "Aucune réponse technique fournie."}
                  </div>

                  {/* Champ de commentaire */}
                  <label>
                    Votre Commentaire<span style={{ color: 'red' }}>*</span>
                  </label>
                  <textarea
                    id={`${presta.id}-${key}-commentaire`}
                    placeholder="Votre commentaire"
                    value={techNotes[presta.id]?.[key]?.commentaire || ''}
                    onChange={e => handleCommentChange(presta.id, key, e.target.value)}
                    className={`input${errors[presta.id]?.[key]?.commentaire ? ' error' : ''}`}
                    disabled={viewOnly}
                  />
                  {errors[presta.id]?.[key]?.commentaire && (
                    <span className="error-message">{errors[presta.id][key].commentaire}</span>
                  )}

                  {/* Sélection de la note */}
                  <div className="note-select-row">
                    <label>Note <span style={{ color: 'red' }}>*</span> :</label>
                    <select
                      id={`${presta.id}-${key}-note`}
                      value={techNotes[presta.id]?.[key]?.note ?? options[0].value}
                      onChange={e => handleNoteChange(presta.id, key, e.target.value)}
                      className={`input note-select${errors[presta.id]?.[key]?.note ? ' error' : ''}`}
                      disabled={viewOnly}
                    >
                      {/* <option value="">-</option> */}
                      {options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <span className="note-out-of">/{maxPoints}</span>
                  </div>
                  {errors[presta.id]?.[key]?.note && (
                    <span className="error-message">{errors[presta.id][key].note}</span>
                  )}
                </div>
              ))}

              {/* Pied de page avec note globale */}
              <div className="presta-footer">
                <div className="presta-footer-left">
                  <div className="note-globale">
                    <h4>Note technique </h4>
                    <div className="note-value">
                      {isExpired ? "-" : `${getNoteGlobale(presta.id)} / 10`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  );
});

export default AnalyseOffreTechnique;